import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Boxes } from "lucide-react";

/**
 * Lightweight inline "add SKU to catalogue" dialog used from the SKU combobox.
 * On success it calls onCreated({sku, alternate_sku, barcode, description}) so the
 * parent can refresh and (optionally) auto-fill the bath record fields.
 */
export const QuickAddSkuDialog = ({ open, onOpenChange, initialSku = "", onCreated }) => {
  const [sku, setSku] = useState(initialSku);
  const [alt, setAlt] = useState("");
  const [barcode, setBarcode] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSku(initialSku || "");
      setAlt("");
      setBarcode("");
      setDescription("");
    }
  }, [open, initialSku]);

  const save = async () => {
    if (!sku.trim() || !description.trim()) {
      toast.error("SKU and Description are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/bath-skus", {
        sku: sku.trim(),
        alternate_sku: alt.trim() || null,
        barcode: barcode.trim() || null,
        description: description.trim(),
      });
      toast.success("SKU added to catalogue");
      onCreated && onCreated(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add SKU");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quick-add-sku-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tighter flex items-center gap-2">
            <Boxes className="h-5 w-5" /> Add SKU to catalogue
          </DialogTitle>
          <DialogDescription>
            Quick-add the new SKU so it's available for autocomplete on every future record.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <Row label="SKU *">
            <Input
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              className="font-mono uppercase"
              autoFocus
              data-testid="quick-add-sku"
            />
          </Row>
          <Row label="Alternate SKU">
            <Input
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              className="font-mono uppercase"
              placeholder="Optional"
              data-testid="quick-add-alt"
            />
          </Row>
          <Row label="Barcode">
            <Input
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
              className="font-mono"
              placeholder="Optional"
              data-testid="quick-add-barcode"
            />
          </Row>
          <Row label="Description *">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="e.g. 24 Chrome jets (12 Jets + 12 Airspa Jets)"
              data-testid="quick-add-description"
            />
          </Row>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="quick-add-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="quick-add-save">
            {saving ? "Adding…" : "Add to catalogue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ label, children }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
